import { Dispatch, SetStateAction } from 'react';
import { Activity, Product, Production, Reservation, Resource, ShortlistLineItem } from 'sr-types/lib/search/v1/graphql';
import { SORT_BY_DATES } from '../../scheduler/schedulerConstants';
import { ENTITY_CONSTANTS } from '../constants/entityConstants';
import type { SearchResponse } from './searchPages';
import { searchByPage } from './searchPages';

type Filter = {
    identifier: string;
    dateMin?: string;
    dateMax?: string;
    maxValueExcluded?: boolean;
    value?: string | number | boolean;
};

export type SingleFilter = {
    filters: Filter[];
    sortBy?: string;
};

const DEFAULT_SORT_BY = SORT_BY_DATES.endDateDESC;

type SearchProps<TItem> = {
    activeOrganizationAccount: string;
    batchSize: number;
    filters: SingleFilter[];
    setBatchState: Dispatch<SetStateAction<TItem[]>>;
    setSearchData?: Dispatch<SetStateAction<TItem[]>>;
    chunkSize?: number;
    includeAllActions?: boolean;
    additionalFragmentName?: string;
    signal?: AbortSignal;
    onFirstPageResponse?: (response: SearchResponse<TItem>) => void;
    requireCompleteResults?: boolean;
};

export function searchPageByType<TItem>(
    resourceType: string,
    {
        activeOrganizationAccount,
        batchSize,
        filters,
        setBatchState,
        setSearchData,
        chunkSize = 2,
        includeAllActions = false,
        additionalFragmentName,
        signal,
        onFirstPageResponse,
        requireCompleteResults = false
    }: SearchProps<TItem>
) {
    const safeSetBatchState: Dispatch<SetStateAction<TItem[]>> = (value) => {
        if (signal?.aborted) return;
        setBatchState(value);
    };

    const safeSetSearchData = setSearchData
        ? (value) => {
              if (signal?.aborted) return;
              setSearchData(value);
          }
        : undefined;

    const createSearch = (filter: Filter[] = [], sortBy?: string) =>
        searchByPage<TItem>(
            resourceType,
            activeOrganizationAccount,
            batchSize,
            filter,
            sortBy ?? DEFAULT_SORT_BY,
            safeSetBatchState,
            safeSetSearchData,
            chunkSize,
            includeAllActions,
            additionalFragmentName,
            signal,
            onFirstPageResponse,
            undefined,
            requireCompleteResults
        );

    if (!filters.length) {
        return [createSearch([])];
    }

    return filters.map(({ filters, sortBy }) => createSearch(filters ?? [], sortBy));
}

export const productSearchByPage = (
    activeOrganizationAccount: string,
    batchSize: number,
    filters: SingleFilter[],
    setBatchState: Dispatch<SetStateAction<Product[]>>,
    setSearchData?: Dispatch<SetStateAction<Product[]>>,
    chunkSize = 2,
    includeAllActions?: boolean,
    additionalFragmentName?: string,
    signal?: AbortSignal,
    onFirstPageResponse?: (response: SearchResponse<Product>) => void
) => {
    return searchPageByType<Product>(ENTITY_CONSTANTS.product, {
        activeOrganizationAccount,
        batchSize,
        filters,
        setBatchState,
        setSearchData,
        chunkSize,
        includeAllActions,
        additionalFragmentName,
        signal,
        onFirstPageResponse
    });
};

export const resourceSearchByPage = (
    activeOrganizationAccount: string,
    batchSize: number,
    filters: SingleFilter[],
    setBatchState: Dispatch<SetStateAction<Resource[]>>,
    setSearchData?: Dispatch<SetStateAction<Resource[]>>,
    chunkSize = 2,
    includeAllActions?: boolean,
    additionalFragmentName?: string,
    signal?: AbortSignal,
    onFirstPageResponse?: (response: SearchResponse<Resource>) => void
) => {
    return searchPageByType<Resource>(ENTITY_CONSTANTS.resource, {
        activeOrganizationAccount,
        batchSize,
        filters,
        setBatchState,
        setSearchData,
        chunkSize,
        includeAllActions,
        additionalFragmentName,
        signal,
        onFirstPageResponse
    });
};

export const productionSearchByPage = (
    activeOrganizationAccount: string,
    batchSize: number,
    filters: SingleFilter[],
    setBatchState: Dispatch<SetStateAction<Production[]>>,
    setSearchData?: Dispatch<SetStateAction<Production[]>>,
    chunkSize = 2,
    includeAllActions?: boolean,
    additionalFragmentName?: string,
    signal?: AbortSignal,
    onFirstPageResponse?: (response: SearchResponse<Production>) => void
) => {
    return searchPageByType<Production>(ENTITY_CONSTANTS.production, {
        activeOrganizationAccount,
        batchSize,
        filters,
        setBatchState,
        setSearchData,
        chunkSize,
        includeAllActions,
        additionalFragmentName,
        signal,
        onFirstPageResponse
    });
};

export const activitySearchByPage = (
    activeOrganizationAccount: string,
    batchSize: number,
    filters: SingleFilter[],
    setBatchState: Dispatch<SetStateAction<Activity[]>>,
    setSearchData?: Dispatch<SetStateAction<Activity[]>>,
    chunkSize = 2,
    includeAllActions?: boolean,
    additionalFragmentName?: string,
    signal?: AbortSignal,
    onFirstPageResponse?: (response: SearchResponse<Activity>) => void,
    requireCompleteResults: boolean = false
) => {
    return searchPageByType<Activity>(ENTITY_CONSTANTS.activity, {
        activeOrganizationAccount,
        batchSize,
        filters,
        setBatchState,
        setSearchData,
        chunkSize,
        includeAllActions,
        additionalFragmentName,
        signal,
        onFirstPageResponse,
        requireCompleteResults
    });
};

export const shortlistLineItemSearchByPage = (
    activeOrganizationAccount: string,
    batchSize: number,
    filters: SingleFilter[],
    setBatchState: Dispatch<SetStateAction<ShortlistLineItem[]>>,
    setSearchData?: Dispatch<SetStateAction<ShortlistLineItem[]>>,
    chunkSize = 2,
    includeAllActions?: boolean,
    additionalFragmentName?: string,
    signal?: AbortSignal,
    onFirstPageResponse?: (response: SearchResponse<ShortlistLineItem>) => void
) => {
    return searchPageByType<ShortlistLineItem>(ENTITY_CONSTANTS.shortlistLineitem, {
        activeOrganizationAccount,
        batchSize,
        filters,
        setBatchState,
        setSearchData,
        chunkSize,
        includeAllActions,
        additionalFragmentName,
        signal,
        onFirstPageResponse
    });
};

export const reservationSearchPage = (
    activeOrganizationAccount: string,
    batchSize: number,
    filters: SingleFilter[],
    setBatchState: Dispatch<SetStateAction<Reservation[]>>,
    setSearchData?: Dispatch<SetStateAction<Reservation[]>>,
    chunkSize = 2,
    includeAllActions = false,
    additionalFragmentName?: string,
    signal?: AbortSignal,
    onFirstPageResponse?: (response: SearchResponse<Reservation>) => void
) => {
    return searchPageByType<Reservation>(ENTITY_CONSTANTS.reservation, {
        activeOrganizationAccount,
        batchSize,
        filters,
        setBatchState,
        setSearchData,
        chunkSize,
        includeAllActions,
        additionalFragmentName,
        signal,
        onFirstPageResponse
    });
};
